import { Link } from 'react-router-dom';
import { useState, useEffect, useMemo, useRef, useCallback, type RefObject } from 'react';
import {
  CalendarHeart,
  CalendarCheck,
  CalendarPlus,
} from 'lucide-react';
import { startOfDay, endOfDay } from 'date-fns';
import type { Reservation } from '~/lib/api/reservations';
import { useAssignedStores } from '@/hooks/useAssignedStores';
import { useReservationsInfinite, type ReservationFilters } from '@/hooks/useReservationsInfinite';
import { useImeSafeSearch } from '@/hooks/useImeSafeSearch';
import { useReservationStats } from '@/hooks/useReservationStats';
import { StatsCardSkeleton } from '@/components/ui/skeleton/stats-card-skeleton';
import { Skeleton } from '@/components/ui/skeleton';
import { usePullToReload } from '@/hooks/usePullToReload';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import { PullToRefreshIndicator } from '@/components/ui/PullToRefreshIndicator';
import { InfiniteScrollSentinel } from '@/components/shared/InfiniteScrollSentinel';
import { withMinDuration } from '@/lib/utils';
import { DatePickerSheet } from '@sakura-grp/ui/date-picker-sheet';

export default function Reservations() {
  const { data: assignedStores = [] } = useAssignedStores();

  // --- フィルタ state ---
  const [showPreReservation, setShowPreReservation] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const search = useImeSafeSearch({onApply: setSearchQuery});
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const storesInitialized = useRef(false);

  // 所属店舗取得後に全店舗を選択（初回のみ）
  useEffect(() => {
    if (assignedStores.length > 0 && !storesInitialized.current) {
      storesInitialized.current = true;
      setSelectedStoreIds(assignedStores.map(s => s.id));
    }
  }, [assignedStores]);

  // 全選択時はフィルタなし
  const storeIdsParam = useMemo(() => {
    if (selectedStoreIds.length === 0 || selectedStoreIds.length === assignedStores.length) {
      return null;
    }
    return selectedStoreIds;
  }, [selectedStoreIds, assignedStores]);

  // 日付フィルタ: 選択された日の 00:00:00 〜 23:59:59 を UTC で送信
  const dateFrom = selectedDate ? startOfDay(selectedDate).toISOString() : null;
  const dateTo = selectedDate ? endOfDay(selectedDate).toISOString() : null;

  const filters: ReservationFilters = {
    search: searchQuery,
    storeIds: storeIdsParam,
    futureOnly: showPreReservation,
    dateFrom,
    dateTo,
    sortAscending: false,
  };

  // --- データ取得 ---
  const {
    data: pagesData,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useReservationsInfinite(filters);

  const {
    data: stats,
    isLoading: isStatsLoading,
    refetch: refetchStats,
  } = useReservationStats();

  // 全ページの予約をフラットに結合
  const reservations = useMemo(
    () => pagesData?.pages.flatMap(p => p.reservations) ?? [],
    [pagesData]
  );

  // --- Pull to Refresh ---
  const { ref, refreshing, isReady } = usePullToReload({
    onRefresh: () => withMinDuration(async () => {
      await Promise.all([refetch(), refetchStats()]);
    }),
    onError: (error) => {
      console.error("予約データの更新に失敗しました:", error);
    }
  });

  useScrollRestoration(ref as RefObject<HTMLElement>);

  const handleLoadMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const toggleStore = (storeId: string) => {
    setSelectedStoreIds((prev) =>
      prev.includes(storeId)
        ? prev.filter((id) => id !== storeId)
        : [...prev, storeId]
    );
  };

  const handleDateSelect = (day: Date | undefined) => {
    setSelectedDate(day);
  };

  const getReservationIcon = (type: Reservation['type']) => {
    switch (type) {
      case '同伴':
        return <CalendarHeart strokeWidth={1.5} className="translate-y-xs size-[2.4rem] flex-none text-brand" />;
      case '確約':
        return <CalendarCheck strokeWidth={1.5} className="translate-y-xs size-[2.4rem] flex-none text-brand" />;
      case '予約':
        return <CalendarPlus strokeWidth={1.5} className="translate-y-xs size-[2.4rem] flex-none text-brand" />;
    }
  };

  const isDateActive = !!selectedDate;

  return (
    <main className="h-full flex flex-col">
      <div className="px-lg h-3xl flex items-center pb-[1.4rem] flex-shrink-0">
        <h1 className="text-3xl">予約履歴</h1>
      </div>
      <div
        ref={ref as any}
        className="flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain [touch-action:pan-y] [-webkit-overflow-scrolling:touch] border-t bg-white"
      >
        {/* コンテンツラッパー：CSS変数でインジケーターとコンテンツを一緒に移動 */}
        <div
          className="min-h-full will-change-transform"
          style={{
            transform: `translateY(var(--pull-offset, 0px))`,
            transition: 'var(--pull-transition, none)'
          }}
        >
          {/* Pull to Refreshインジケーター */}
          <PullToRefreshIndicator refreshing={refreshing} isReady={isReady} />

          {error && (
            <div className="px-lg py-md text-center text-red-500">
              予約データの読み込みに失敗しました
            </div>
          )}

        {/* Stats Section */}
        {isStatsLoading ? (
          <StatsCardSkeleton />
        ) : stats ? (
          <div className="px-lg py-md gap-lg flex w-full items-center justify-evenly border-b">
            <div className="gap-sm grid place-items-center">
              <h2 className="text-sm text-brand">🏆 {new Date().getFullYear()}年の予約</h2>
              <ul className="gap-sm flex text-xl font-bold tracking-tighter">
                <li className="gap-sm inline-flex items-center">
                  <CalendarHeart className="size-xl text-brand" />
                  <span className="text-center leading-none text-brand">
                    <strong className="block text-sm font-bold tracking-normal opacity-70">同伴</strong>
                    {stats.year.同伴}
                  </span>
                </li>
                <li className="gap-sm inline-flex items-center">
                  <CalendarCheck className="size-xl text-brand" />
                  <span className="text-center leading-none text-brand">
                    <strong className="block text-sm font-bold tracking-normal opacity-70">確約</strong>
                    {stats.year.確約}
                  </span>
                </li>
                <li className="gap-sm inline-flex items-center">
                  <CalendarPlus className="size-xl text-brand" />
                  <span className="text-center leading-none text-brand">
                    <strong className="block text-sm font-bold tracking-normal opacity-70">予約</strong>
                    {stats.year.予約}
                  </span>
                </li>
              </ul>
            </div>
            <div className="gap-sm grid place-items-center">
              <h2 className="text-sm text-stone-400">🏆 これまでの予約</h2>
              <ul className="gap-sm flex font-bold tracking-tighter">
                <li className="gap-sm inline-flex items-center">
                  <CalendarHeart className="size-lg text-stone-600" />
                  <span className="text-center leading-none text-stone-600">
                    <strong className="block text-xs font-bold tracking-normal opacity-70">同伴</strong>
                    {stats.allTime.同伴}
                  </span>
                </li>
                <li className="gap-sm inline-flex items-center">
                  <CalendarCheck className="size-lg text-stone-600" />
                  <span className="text-center leading-none text-stone-600">
                    <strong className="block text-xs font-bold tracking-normal opacity-70">確約</strong>
                    {stats.allTime.確約}
                  </span>
                </li>
                <li className="gap-sm inline-flex items-center">
                  <CalendarPlus className="size-lg text-stone-600" />
                  <span className="text-center leading-none text-stone-600">
                    <strong className="block text-xs font-bold tracking-normal opacity-70">予約</strong>
                    {stats.allTime.予約}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        ) : null}

        {/* Filter Controls */}
        <div className="px-lg gap-md h-(--h-list) flex items-center border-b">
          <label className="space-y-sm grid cursor-pointer place-items-center leading-none">
            <input
              type="checkbox"
              checked={showPreReservation}
              onChange={(e) => setShowPreReservation(e.target.checked)}
              className="peer sr-only"
            />
            <div className="peer relative h-6 w-11 rounded-full border-gray-500 bg-gray-400 after:absolute after:start-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand"></div>
            <p className="text-2xs -mx-sm text-center font-bold tracking-tight text-stone-400 peer-checked:text-brand">
              予約前
            </p>
          </label>
          <input
            type="text"
            className="h-xl bg-input/10 text-md shadow-xs selection:bg-primary selection:text-primary-foreground file:text-foreground placeholder:text-muted-foreground/50 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 flex min-w-0 flex-1 rounded-lg border px-2 py-1 outline-none transition-[color,box-shadow] file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
            placeholder="名前・カナ・会社名で検索"
            {...search.inputProps}
          />
          <button
            type="button"
            className={`h-xl p-md shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:border-ring focus-visible:ring-ring/50 inline-flex items-center justify-start whitespace-nowrap rounded-md border text-left text-xs font-normal tracking-tight outline-none transition-all focus-visible:ring-[3px] ${isDateActive ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-background'}`}
            onClick={() => setCalendarOpen(true)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-calendar size-4"
              aria-hidden="true"
            >
              <path d="M8 2v4"></path>
              <path d="M16 2v4"></path>
              <rect width="18" height="18" x="3" y="4" rx="2"></rect>
              <path d="M3 10h18"></path>
            </svg>
          </button>
          <DatePickerSheet
            open={calendarOpen}
            onOpenChange={setCalendarOpen}
            value={selectedDate}
            onChange={handleDateSelect}
            title="予約日を選択"
          />
          {assignedStores.length > 0 && (
            <div
              className="!divide-(--border) flex flex-none items-center divide-x rounded-lg border bg-white text-xs font-bold"
            >
              {assignedStores.map((store) => (
                <div key={store.id} className="gap-xs px-md flex items-center justify-center">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={selectedStoreIds.includes(store.id)}
                    data-state={selectedStoreIds.includes(store.id) ? 'checked' : 'unchecked'}
                    onClick={() => toggleStore(store.id)}
                    className="size-(--bigger-sm) border-input shadow-xs focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground peer rounded-sm border bg-white outline-none transition-shadow focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50"
                    id={`store-${store.id}`}
                  >
                    {selectedStoreIds.includes(store.id) && (
                      <span className="flex items-center justify-center text-current transition-none">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-check size-md"
                          aria-hidden="true"
                        >
                          <path d="M20 6 9 17l-5-5"></path>
                        </svg>
                      </span>
                    )}
                  </button>
                  <label
                    className="h-xl gap-md flex cursor-pointer select-none items-center text-sm font-bold leading-none tracking-tight peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
                    htmlFor={`store-${store.id}`}
                  >
                    {store.short_name}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reservation List */}
        {isLoading ? (
          <ul className="divide-y divide-stone-200 border-b">
            {Array.from({ length: 5 }).map((_, index) => (
              <li key={index} className="px-lg py-md flex items-center space-x-md">
                <Skeleton className="h-16 w-16" />
                <div className="flex-1 space-y-sm">
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-4 w-64" />
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="divide-y divide-stone-200 border-b">
            {reservations.length === 0 ? (
              <li className="px-lg py-xl text-center text-gray-500">
                該当する予約がありません
              </li>
            ) : reservations.map((reservation) => (
            <li
              key={reservation.id}
              className={`px-lg gap-md h-(--h-list) relative flex items-center leading-none ${
                reservation.isPast ? 'opacity-50' : ''
              }`}
            >
              <strong className="py-sm bg-secondary text-primary text-2xs absolute end-0 top-0 rounded-bl-lg border-b border-s px-[.7em] font-bold leading-snug tracking-tight">
                {reservation.store}
              </strong>
              <div className="text-md space-y-sm text-center tracking-normal text-brand">
                {getReservationIcon(reservation.type)}
                <span className="block font-bold">{reservation.type}</span>
              </div>
              <time className="flex-none items-center text-center text-lg font-bold tracking-tight [.opacity-50_&]:line-through">
                <small className="block">
                  {reservation.date}
                  <span className="text-(length:--smaller-md) ms-xs">({reservation.dayOfWeek})</span>
                </small>
                <small className="text-(length:--bigger-lg) block leading-none">
                  {reservation.time} <span className="text-(length:--smaller-lg)">~</span>
                </small>
              </time>
              <Link to={`/customers/${reservation.customerId}`} className="space-y-sm">
                <div className="gap-sm flex items-center">
                  {reservation.points !== undefined ? (
                    <div className="space-y-sm float-none w-[2.45rem] text-center text-[2rem]">
                      <div>{reservation.customerType}</div>
                      <div className="text-sm text-brand">{reservation.points}pt</div>
                    </div>
                  ) : (
                    <div className="float-none w-[2.45rem] text-center text-[2.45rem]">
                      {reservation.customerType}
                    </div>
                  )}
                  <div className="space-y-xs leading-none">
                    <h3 className="text-xl">
                      {reservation.customerName}
                      <small className="ms-xs">({reservation.guestCount})</small>
                    </h3>
                    {reservation.company && (
                      <small className="mx-xs text-(length:--smaller-xl) line-clamp-1 tracking-normal">
                        ( {reservation.company} )
                      </small>
                    )}
                  </div>
                </div>
              </Link>
            </li>
          ))}
          </ul>
        )}

        {/* 無限スクロール sentinel */}
        <InfiniteScrollSentinel
          onLoadMore={handleLoadMore}
          hasNextPage={hasNextPage ?? false}
          isFetchingNextPage={isFetchingNextPage}
        />
        </div>
      </div>
    </main>
  );
}
